/* ═══════════════════════════════════════════════════════════════
   PiARIS — Audit Page Section
   Per-page grouping wrapper. Shows: URL title, issue count,
   optional "Core User Path" tag, page thumbnail. Contains all
   the AuditIssueCards for that page.
   © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */
import React from "react";
import { P } from "../constants";

var E = React.createElement;

/** AuditPageSection — page header + issue list wrapper
    Props:
      page:        the wp[i] object (has url, n, totalIssues, critCount, etc.)
      pageIdx:     index in wp
      children:    the rendered AuditIssueCard nodes
      issueCount:  filtered count to show
      thumbnail:   optional image URL for this page
      isCore:      bool — mark as "Core User Path" */
function AuditPageSection(p) {
  var pg = p.page;
  var url = pg.url || "";
  var name = pg.n || "Untitled Page";
  var count = p.issueCount != null ? p.issueCount : (pg.issues || []).length;

  return E("section", {
    role: "region",
    "aria-label": name + " \u2014 " + count + " issues",
    className: "auditPageSection",
    style: {
      marginBottom: 28,
      paddingBottom: 4
    }
  },
    /* Page header row */
    E("header", {
      style: {
        display: "grid",
        gridTemplateColumns: p.thumbnail ? "1fr 120px" : "1fr",
        gap: 16,
        alignItems: "flex-start",
        padding: "14px 0 12px",
        borderBottom: "2px solid " + P.bdr,
        marginBottom: 14
      }
    },
      /* Title block */
      E("div", { style: { minWidth: 0 } },
        E("h2", {
          style: {
            fontSize: 22,
            fontWeight: 900,
            margin: "0 0 4px",
            color: P.slate,
            lineHeight: 1.25
          }
        },
          name
        ),
        url ? E("a", {
          href: url,
          target: "_blank",
          rel: "noopener noreferrer",
          style: {
            fontSize: 11,
            color: P.pri,
            textDecoration: "underline",
            wordBreak: "break-all",
            display: "inline-block",
            marginBottom: 8
          }
        }, url) : null,
        E("div", { style: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 } },
          E("span", {
            style: {
              padding: "3px 10px",
              borderRadius: 6,
              background: P.sCritBg,
              color: P.sCrit,
              fontSize: 11,
              fontWeight: 800
            }
          }, count + " " + (count === 1 ? "issue" : "issues")),
          p.isCore ? E("span", {
            style: {
              padding: "3px 10px",
              borderRadius: 6,
              background: P.priLt,
              color: P.pri,
              fontSize: 10,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: ".04em"
            }
          }, "Core User Path") : null,
          pg.critCount && pg.critCount > 0 ? E("span", {
            style: {
              padding: "3px 10px",
              borderRadius: 6,
              background: P.sCritBg,
              color: P.sCrit,
              fontSize: 10,
              fontWeight: 800
            }
          }, pg.critCount + " critical") : null,
          pg.isArchived ? E("span", {
            style: {
              padding: "3px 10px",
              borderRadius: 6,
              background: P.amberLt,
              color: P.amber,
              fontSize: 10,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: ".04em"
            }
          }, "\uD83D\uDCE6 Archived") : null
        )
      ),
      /* Thumbnail */
      p.thumbnail ? E("img", {
        src: p.thumbnail,
        alt: "Thumbnail of " + name,
        style: {
          width: 120,
          height: 80,
          objectFit: "cover",
          objectPosition: "top",
          borderRadius: 6,
          border: "1px solid " + P.bdr
        }
      }) : null
    ),
    /* Issue cards */
    p.children
  );
}

export { AuditPageSection };
